import React from 'react';
import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { CaretRight } from 'phosphor-react';
import { useNavigate } from 'react-router-dom';

import {
  FechStatus,
  IContaCilFechEmpresa,
  useContaCilFechamentoDetalhe,
} from '../../../../../hooks/useContaCilDashboard';

const MESES_FULL = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];
const BASE = '/modulesHome/accountingConciliation/conciliacao-contabil';

const formatNumber = (n: number): string =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(n);

const STATUS_META: Record<
  FechStatus,
  { label: string; cor: string; acao: string; rota: (id: number) => string }
> = {
  a_fechar: {
    label: 'Só falta fechar',
    cor: '#0EA371',
    acao: 'Ver empresa',
    rota: id => `${BASE}/empresas?empresa=${id}`,
  },
  a_exportar: {
    label: 'A exportar',
    cor: '#3B7DDD',
    acao: 'Exportar',
    rota: id => `${BASE}/exportar?empresa=${id}`,
  },
  nao_conciliado: {
    label: 'Não conciliado',
    cor: '#E08A06',
    acao: 'Conciliar',
    rota: id => `${BASE}/conciliar?empresa=${id}`,
  },
};

const EmpresaRow = ({ e, onClose }: { e: IContaCilFechEmpresa; onClose: () => void }) => {
  const navigate = useNavigate();
  const meta = STATUS_META[e.status];
  return (
    <Flex
      as="button"
      w="full"
      textAlign="left"
      align="center"
      gap={3}
      px={3}
      py={3}
      borderRadius="lg"
      borderWidth="1px"
      borderColor="rgba(15,23,42,0.06)"
      bg="rgba(15,23,42,0.015)"
      _hover={{ bg: 'rgba(124,58,237,0.05)', borderColor: 'rgba(124,58,237,0.25)' }}
      onClick={() => {
        onClose();
        navigate(meta.rota(e.empresa_id));
      }}
    >
      <Box flex={1} minW={0}>
        <Flex align="center" gap={2}>
          <Text fontSize="13px" fontWeight="600" color="#0F172A" noOfLines={1}>
            {e.razao_social || `Empresa ${e.empresa_id}`}
          </Text>
          {e.uf && (
            <Text fontSize="10px" color="#94A3B8" flexShrink={0}>
              {e.uf}
            </Text>
          )}
        </Flex>
        <Flex align="center" gap={2} mt={1.5}>
          <Box
            flex={1}
            maxW="120px"
            h="5px"
            borderRadius="full"
            bg="rgba(15,23,42,0.06)"
            overflow="hidden"
          >
            <Box
              h="full"
              w={`${e.percent_conciliado}%`}
              bg={e.percent_conciliado >= 100 ? '#0EA371' : '#7C3AED'}
            />
          </Box>
          <Text fontSize="10px" color="#64748B" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {e.percent_conciliado.toFixed(0)}% · {formatNumber(e.lancamentos)} lanç.
            {e.pendentes > 0 && ` · ${formatNumber(e.pendentes)} pend.`}
          </Text>
        </Flex>
      </Box>
      <Flex direction="column" align="flex-end" gap={1} flexShrink={0}>
        <Text
          fontSize="10px"
          fontWeight="700"
          color={meta.cor}
          bg={`${meta.cor}14`}
          px={2}
          py={0.5}
          borderRadius="full"
          whiteSpace="nowrap"
        >
          {meta.label}
        </Text>
        <Flex align="center" gap={0.5} color="#7C3AED">
          <Text fontSize="10px" fontWeight="600">
            {meta.acao}
          </Text>
          <CaretRight size={11} weight="bold" />
        </Flex>
      </Flex>
    </Flex>
  );
};

interface FechamentoDetalheModalProps {
  ano: number;
  mes: number | null;
  isOpen: boolean;
  onClose: () => void;
}

export const FechamentoDetalheModal = ({
  ano,
  mes,
  isOpen,
  onClose,
}: FechamentoDetalheModalProps) => {
  const { data, isLoading } = useContaCilFechamentoDetalhe(ano, isOpen ? mes : null);

  const renderBody = () => {
    if (isLoading || !data) {
      return (
        <Flex justify="center" py={10}>
          <Spinner color="#7C3AED" />
        </Flex>
      );
    }
    if (data.empresas.length === 0) {
      return (
        <Flex direction="column" align="center" py={10} gap={2}>
          <Text fontSize="2xl">🎉</Text>
          <Text fontSize="sm" color="#64748B" textAlign="center">
            Tudo fechado nessa competência.
          </Text>
        </Flex>
      );
    }
    return (
      <Stack spacing={2}>
        {data.empresas.map(e => (
          <EmpresaRow key={e.empresa_id} e={e} onClose={onClose} />
        ))}
      </Stack>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered scrollBehavior="inside">
      <ModalOverlay bg="rgba(15,23,42,0.45)" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="20px" overflow="hidden">
        <ModalHeader pb={2}>
          <Text
            fontSize="10px"
            fontWeight="600"
            color="#94A3B8"
            textTransform="uppercase"
            letterSpacing="0.08em"
          >
            Fechamento contábil
          </Text>
          <Text fontSize="20px" fontWeight="700" color="#0F172A" letterSpacing="-0.01em">
            {mes ? MESES_FULL[mes - 1] : ''} / {ano}
          </Text>
          {data && (
            <Text fontSize="12px" color="#64748B" mt={1} fontWeight="500">
              <b style={{ color: '#E08A06' }}>{formatNumber(data.pendentes)}</b> pendentes ·{' '}
              {formatNumber(data.fechadas)} fechadas · {formatNumber(data.operaram)} operaram
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton borderRadius="full" top={4} />
        <ModalBody pb={6}>{renderBody()}</ModalBody>
      </ModalContent>
    </Modal>
  );
};
